import  { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import CreateInstitution from '../components/dashboard/supervisor/CreateInstitution';
import CreateSecretary from '../components/dashboard/supervisor/CreateSecretary';
import { useInstitutionsCollection, useUsersCollection, useTransactionsCollection } from '../hooks/useFirestore';
import { useAuth } from '../hooks/useAuth';
import {
  Building,
  UserPlus,
  PlusCircle,
  LogOut,
  DollarSign,
  ArrowDownCircle,
  ArrowUpCircle,
  Calendar,
  Edit,
  Pause,
  Play,
  Trash2,
  Eye
} from 'lucide-react';

interface Institution {
  id: string;
  name: string;
  status: 'active' | 'inactive' | 'pending';
  createdAt: any;
  city?: string;
  institutionName?: string; // Ajout pour correspondre à la structure des secrétaires
  currency?: {
    code: string;
    symbol: string;
    name: string;
  };
}

interface User {
  id: string;
  fullName: string;
  email: string;
  role: string;
  institutionName: string;
  createdAt: any;
  institutionId?: string;
}

interface Transaction {
  id: string;
  institutionId: string;
  type: 'income' | 'expense';
  amount: number;
  description: string;
  createdAt: any;
}

const SupervisorDashboard: React.FC = () => {
  const [showInstitutionModal, setShowInstitutionModal] = useState(false);
  const [showSecretaryModal, setShowSecretaryModal] = useState(false);
  const [, setSelectedInstitution] = useState<Institution | null>(null);

  // Utilisation des hooks spécifiques à votre useFirestore
  const {
    data: institutionsData,
    loading: institutionsLoading,
    getDocuments: getInstitutions,
    updateDocument,
    deleteDocument
  } = useInstitutionsCollection();

  const {
    data: usersData,
    loading: usersLoading,
    getDocuments: getUsers
  } = useUsersCollection();

  const {
    data: transactionsData,
    loading: transactionsLoading,
    getDocuments: getTransactions
  } = useTransactionsCollection();

  const { user, logout } = useAuth();

  // Charger les données avec des requêtes spécifiques
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Charger les institutions (toutes)
        await getInstitutions();

        // Charger les secrétaires (filtre par rôle)
        await getUsers({
          field: 'role',
          operator: '==',
          value: 'secretary'
        });

        // Charger toutes les transactions
        await getTransactions({
          orderByField: 'createdAt',
          orderDir: 'desc'
        });

      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      }
    };

    fetchData();
  }, [getInstitutions, getUsers, getTransactions]);

  useEffect(() => {
    const handleInstitutionCreated = () => {
      // Recharger les données quand une nouvelle institution est créée
      getInstitutions();
      getTransactions();
      getUsers({
        field: 'role',
        operator: '==',
        value: 'secretary'
      });
    };

    // Écouter l'événement personnalisé
    window.addEventListener('institution-created', handleInstitutionCreated);

    // Nettoyer l'écouteur d'événement
    return () => {
      window.removeEventListener('institution-created', handleInstitutionCreated);
    };
  }, [getInstitutions, getTransactions, getUsers]);

  useEffect(() => {
    const handleSecretaryCreated = () => {
      // Recharger les secrétaires quand un nouveau est créé
      getUsers({
        field: 'role',
        operator: '==',
        value: 'secretary'
      });
    };

    // Écouter l'événement personnalisé
    window.addEventListener('secretary-created', handleSecretaryCreated);

    // Nettoyer l'écouteur d'événement
    return () => {
      window.removeEventListener('secretary-created', handleSecretaryCreated);
    };
  }, [getUsers]);

  // Convertir les données en types spécifiques
  const institutions: Institution[] = (institutionsData as Institution[]) || [];
  const allUsers: User[] = (usersData as User[]) || [];
  const transactions: Transaction[] = (transactionsData as Transaction[]) || [];

  // Filtrer les secrétaires
  const secretaries = allUsers.filter(user => user.role === 'secretary');

  // Calculer les statistiques par institution
  const calculateInstitutionStats = (institutionId: string) => {
    const institutionTransactions = transactions.filter(
      t => t.institutionId === institutionId
    );

    const totalIncome = institutionTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const totalExpense = institutionTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const balance = totalIncome - totalExpense;

    // Trouver la dernière transaction
    const lastTransaction = institutionTransactions.length > 0
      ? institutionTransactions[0] // Déjà trié par getDocuments
      : null;

    return {
      totalIncome,
      totalExpense,
      balance,
      lastTransactionDate: lastTransaction?.createdAt
    };
  };

  // Obtenir le nom de l'institution pour un secrétaire
  // Dans votre composant, remplacez la fonction getInstitutionName par :

  // Obtenir le nom de l'institution pour un secrétaire
  // Obtenir le nom de l'institution pour un secrétaire
  const getInstitutionName = (institutionName?: string) => {
    if (!institutionName) return 'Non assigné';

    // Si vous stockez le nom de l'institution dans users
    // Vérifier si ce nom correspond à une institution existante
    const institutionExists = institutions.some(inst =>
      inst.name === institutionName ||
      inst.institutionName === institutionName
    );

    if (institutionExists) {
      return institutionName;
    }

    return `${institutionName}`;
  };

  // Formater une date Firestore
  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return dateObj.toLocaleDateString('fr-FR');
    } catch {
      return 'N/A';
    }
  };

  // Formater le symbole de devise
  const getCurrencySymbol = (currency?: { code: string; symbol: string; name: string }) => {
    if (!currency) return 'CFA';
    return currency.symbol || currency.code || 'CFA';
  };

  // Toggle le statut d'une institution
  const toggleInstitutionStatus = async (institution: Institution) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir ${institution.status === 'active' ? 'suspendre' : 'activer'} cette institution ?`)) {
      return;
    }

    try {
      const newStatus = institution.status === 'active' ? 'inactive' : 'active';
      await updateDocument(institution.id, {
        status: newStatus,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Erreur lors de la modification du statut:', error);
      alert('Une erreur est survenue lors de la modification.');
    }
  };

  // Supprimer une institution
  const deleteInstitution = async (institution: Institution) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer définitivement l'institution "${institution.name}" ? Cette action est irréversible.`)) {
      return;
    }

    try {
      await deleteDocument(institution.id);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Une erreur est survenue lors de la suppression.');
    }
  };

  // Voir les détails d'une institution
  const viewInstitutionDetails = (institution: Institution) => {
    setSelectedInstitution(institution);
    alert(`Détails de l'institution:\n\nNom: ${institution.name}\nStatut: ${institution.status}\nDevise: ${getCurrencySymbol(institution.currency)}\nLocalisation: ${institution.city || 'Non spécifiée'}\nDate de création: ${formatDate(institution.createdAt)}`);
  };

  // Modifier une institution
  const editInstitution = (institution: Institution) => {
    setSelectedInstitution(institution);
    alert(`Modifier l'institution: ${institution.name}\n\nCette fonctionnalité sera bientôt disponible.`);
  };

  const handleLogout = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      await logout();
      window.location.href = '/login';
    }
  };

  const isLoading = institutionsLoading || usersLoading || transactionsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header avec bouton de déconnexion */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Tableau de bord Superviseur
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Bonjour, {user?.displayName || user?.email}
          </p>
        </div>

        <Button
          variant="outline"
          onClick={handleLogout}
          className="flex items-center px-4 py-2 text-red-600 hover:bg-red-50 border-red-200"
        >
          <LogOut className="h-5 w-5 mr-2" />
          Déconnexion
        </Button>
      </div>

      {/* Section 1: Liste des institutions */}
      <Card>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Building className="h-5 w-5 mr-2" />
              Institutions ({institutions.length})
            </h2>
            <Button
              variant="primary"
              onClick={() => setShowInstitutionModal(true)}
              className="flex items-center"
            >
              <PlusCircle className="h-5 w-5 mr-2" />
              Créer une institution
            </Button>
          </div>

          {institutions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nom
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Devise
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Localisation
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date de création
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions rapides
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {institutions.map((institution) => (
                    <tr key={institution.id}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {institution.name}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${institution.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : institution.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                          }`}>
                          {institution.status === 'active' ? 'Actif' :
                            institution.status === 'pending' ? 'En attente' : 'Inactif'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <span className="font-medium text-gray-700">
                            {getCurrencySymbol(institution.currency)}
                          </span>
                          {institution.currency?.code && (
                            <span className="ml-1 text-xs text-gray-500">
                              ({institution.currency.code})
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {institution.city || 'Non spécifiée'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDate(institution.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {/* Bouton Voir détails */}
                          <button
                            type="button"
                            onClick={() => viewInstitutionDetails(institution)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                            title="Voir détails"
                          >
                            <Eye className="h-4 w-4" />
                          </button>

                          {/* Bouton Modifier */}
                          <button
                            type="button"
                            onClick={() => editInstitution(institution)}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>

                          {/* Bouton Suspendre/Activer */}
                          {institution.status === 'active' ? (
                            <button
                              type="button"
                              onClick={() => toggleInstitutionStatus(institution)}
                              className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded-full transition-colors"
                              title="Suspendre"
                            >
                              <Pause className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => toggleInstitutionStatus(institution)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                              title="Activer"
                            >
                              <Play className="h-4 w-4" />
                            </button>
                          )}

                          {/* Bouton Supprimer */}
                          <button
                            type="button"
                            onClick={() => deleteInstitution(institution)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Building className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                Aucune institution pour le moment
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Section 2: Liste des secrétaires */}
      <Card>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <UserPlus className="h-5 w-5 mr-2" />
              Secrétaires ({secretaries.length})
            </h2>
            <Button
              variant="secondary"
              onClick={() => setShowSecretaryModal(true)}
              className="flex items-center"
            >
              <PlusCircle className="h-5 w-5 mr-2" />
              Créer un secrétaire
            </Button>
          </div>

          {secretaries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nom
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome de l'Institution
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID de l'Institution
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date d'ajout
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {secretaries.map((secretary) => (
                    <tr key={secretary.id}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {secretary.fullName || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {secretary.email}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {getInstitutionName(secretary.institutionName)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {getInstitutionName(secretary.institutionId)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDate(secretary.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <UserPlus className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                Aucun secrétaire pour le moment
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Section 3: Situation financière par institution */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <DollarSign className="h-5 w-5 mr-2" />
            Situation financière par institution
          </h2>

          {institutions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Institution
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Devise
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Entrées
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Sorties
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Solde
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dernière opération
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {institutions.map((institution) => {
                    const stats = calculateInstitutionStats(institution.id);
                    const currencySymbol = getCurrencySymbol(institution.currency);

                    return (
                      <tr key={institution.id}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">
                            {institution.name}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <span className="font-medium text-gray-700">
                              {currencySymbol}
                            </span>
                            {institution.currency?.code && (
                              <span className="ml-1 text-xs text-gray-500">
                                ({institution.currency.code})
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center text-green-600">
                            <ArrowDownCircle className="h-4 w-4 mr-2" />
                            <span className="font-medium">
                              {stats.totalIncome.toLocaleString('fr-FR')} {currencySymbol}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center text-red-600">
                            <ArrowUpCircle className="h-4 w-4 mr-2" />
                            <span className="font-medium">
                              {stats.totalExpense.toLocaleString('fr-FR')} {currencySymbol}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-medium ${stats.balance >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                            {stats.balance.toLocaleString('fr-FR')} {currencySymbol}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {stats.lastTransactionDate ? (
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              {formatDate(stats.lastTransactionDate)}
                            </div>
                          ) : (
                            'Aucune opération'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                Aucune donnée financière disponible
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Modals */}
      <Modal
        isOpen={showInstitutionModal}
        onClose={() => setShowInstitutionModal(false)}
        title="Créer une nouvelle institution"
        size="lg"
      >
        <CreateInstitution
          onSuccess={() => setShowInstitutionModal(false)}
          onCancel={() => setShowInstitutionModal(false)}
        />
      </Modal>

      <Modal
        isOpen={showSecretaryModal}
        onClose={() => setShowSecretaryModal(false)}
        title="Créer un nouveau secrétaire"
        size="lg"
      >
        <CreateSecretary
          onSuccess={() => setShowSecretaryModal(false)}
          onCancel={() => setShowSecretaryModal(false)}
        />
      </Modal>
    </div>
  );
};

export default SupervisorDashboard;